import React, { useState, useRef, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import TicketMessageItem from './TicketMessageItem';

const TicketMessagesStream = ({
  messages,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  error
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const [lightboxImage, setLightboxImage] = useState(null);
  const streamContainerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isPrependRef = useRef(false);

  // Auto-scroll al fondo para mensajes nuevos o carga inicial, y preservar posición al cargar anteriores
  useEffect(() => {
    const container = streamContainerRef.current;
    if (!container) return;

    if (isPrependRef.current) {
      // Preservar la posición del scroll al cargar mensajes anteriores arriba
      const diff = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop = diff;
      isPrependRef.current = false;
    } else {
      // Si es carga inicial o mensaje nuevo al fondo
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Infinite scroll automático hacia arriba al llegar cerca del tope
  const handleScroll = () => {
    const container = streamContainerRef.current;
    if (!container || isLoadingMore || !hasMore || isLoading) return;

    if (container.scrollTop < 60) {
      prevScrollHeightRef.current = container.scrollHeight;
      isPrependRef.current = true;
      onLoadMore();
    }
  };

  return (
    <div
      ref={streamContainerRef}
      onScroll={handleScroll}
      className="flex-grow-1 p-4 overflow-y-auto d-flex flex-column gap-3"
      style={{
        backgroundColor: isDark ? '#0b141a' : '#ffffff',
        height: '100%',
        minHeight: 0,
        overflowY: 'auto'
      }}
    >
      {/* Botón / Spinner de carga para mensajes anteriores */}
      {isLoadingMore && (
        <div className="text-center text-muted my-2" style={{ fontSize: '0.8rem' }}>
          <div className="spinner-border spinner-border-sm me-2 text-success" role="status"></div>
          Cargando mensajes anteriores...
        </div>
      )}

        {hasMore && !isLoadingMore && !isLoading && messages.length >= 30 && (
          <div className="text-center my-1">
            <button
              type="button"
              className="btn btn-sm btn-link text-muted text-decoration-none shadow-none"
              style={{ fontSize: '0.75rem' }}
              onClick={onLoadMore}
            >
              ▲ Cargar mensajes anteriores
            </button>
          </div>
        )}

        {isLoading && (
          <div className="text-center text-muted my-3" style={{ fontSize: '0.85rem' }}>
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Cargando mensajes...
          </div>
        )}

        {error && (
          <div className="alert alert-danger mx-3 my-2 text-center" style={{ fontSize: '0.85rem' }} role="alert">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
            {error}
          </div>
        )}

        {!isLoading && !error && messages.length === 0 && (
          <div className="text-center text-muted my-3" style={{ fontSize: '0.85rem' }}>
            No hay mensajes en este ticket aún.
          </div>
        )}

        {/* Centered Date Tag */}
        {messages.length > 0 && (
          <div className="text-center my-1">
            <span
              className="px-3 py-1 rounded-pill fw-semibold"
              style={{
                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: '0.72rem'
              }}
            >
              Historial de Conversación
            </span>
          </div>
        )}

        {/* Lista de Mensajes */}
        {messages.map((msg, index) => (
          <TicketMessageItem
            key={msg.idMensaje || msg.id || index}
            msg={msg}
            index={index}
            onOpenLightbox={setLightboxImage}
          />
        ))}

      {/* Modal Lightbox Viewer en la misma pantalla */}
      {lightboxImage && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div
            className="position-relative d-flex flex-column align-items-center p-3"
            style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Controles del visor */}
            <div className="w-100 d-flex justify-content-end gap-2 mb-2">
              <a
                href={lightboxImage}
                download="imagen_whatsapp.jpg"
                className="btn btn-sm btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: '36px', height: '36px' }}
                title="Descargar imagen"
              >
                <FontAwesomeIcon icon={faDownload} />
              </a>
              <button
                className="btn btn-sm btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: '36px', height: '36px' }}
                onClick={() => setLightboxImage(null)}
                title="Cerrar (Esc)"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Imagen ampliada */}
            <img
              src={lightboxImage}
              alt="Vista ampliada"
              className="rounded-3 shadow-lg img-fluid"
              style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketMessagesStream;
